// ----- Phase 5.X-publish-governance — Modal #3: Test vs Final intent -----
//
// Operator MUST pick either Test or Final. There is no third option
// or skip path — the publish-intent is part of the audit record and
// determines whether the sequence number is prefixed with `TEST-`.
// (The prefix happens in the Compiler service, not here; this modal
// only captures the operator's stated intent.)

import { useState, type ReactElement } from 'react';
import { ModalShell } from './modalShell';
import {
  buttonRowStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from './modalShellStyles';

export interface PublishIntentModalProps {
  readonly onContinue: (mode: 'test' | 'final') => void;
  readonly onCancel: () => void;
}

export function PublishIntentModal({
  onContinue,
  onCancel,
}: PublishIntentModalProps): ReactElement {
  const [mode, setMode] = useState<'test' | 'final' | null>(null);

  return (
    <ModalShell testId="pgov-intent-modal" title="Publish intent">
      <p style={{ margin: 0, fontSize: 13 }}>
        Is this a real (final) submission, or a test compile to inspect
        the output?
      </p>
      <fieldset
        style={{
          border: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <label
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            padding: 8,
            border: '1px solid var(--border)',
            borderRadius: 6,
            cursor: 'pointer',
            background: mode === 'test' ? 'rgba(245,158,11,0.10)' : 'var(--surface)',
          }}
        >
          <input
            type="radio"
            data-testid="pgov-intent-test"
            name="publish-mode"
            checked={mode === 'test'}
            onChange={() => setMode('test')}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Test publication</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Sequence # is prefixed with <code>TEST-</code>. Audit log
              records the intent so test runs can be filtered out.
            </div>
          </div>
        </label>
        <label
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            padding: 8,
            border: '1px solid var(--border)',
            borderRadius: 6,
            cursor: 'pointer',
            background: mode === 'final' ? 'rgba(34,197,94,0.10)' : 'var(--surface)',
          }}
        >
          <input
            type="radio"
            data-testid="pgov-intent-final"
            name="publish-mode"
            checked={mode === 'final'}
            onChange={() => setMode('final')}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Final publication</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Real submission. Sequence # is used as-is. Recorded in the
              audit log as the binding publish act.
            </div>
          </div>
        </label>
      </fieldset>
      <div style={buttonRowStyle}>
        <button
          type="button"
          data-testid="pgov-intent-cancel"
          onClick={onCancel}
          style={secondaryButtonStyle}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="pgov-intent-continue"
          disabled={mode === null}
          onClick={() => mode && onContinue(mode)}
          style={{
            ...primaryButtonStyle,
            opacity: mode === null ? 0.5 : 1,
            cursor: mode === null ? 'not-allowed' : 'pointer',
          }}
        >
          Continue
        </button>
      </div>
    </ModalShell>
  );
}
